import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { useToast } from './ui/toast';
import { UserRole } from 'utils/bankStatements';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { reconcileStatementAccountAction } from 'actions/bankStatements';

const formatCurrency = amount => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format(amount);

const ReconcileButton = ({
  statementId,
  accountId,
  userRole,
  isReconciled = false,
  accountNumber,
}) => {
  const dispatch = useDispatch();
  const bankProcessorKey = useSelector((state) => get(state, 'workflow.data.data.bankProcessorKey') || get(state, 'workflow.state.data.bankProcessorKey'));
  const applicationId = useSelector((state) => get(state, 'workflow.attributes.entityId'));
  const [loading, setLoading] = useState(false);
  const [reconciliationStatus, setReconciliationStatus] = useState(isReconciled);
  const [showDetails, setShowDetails] = useState(false);
  const [details, setDetails] = useState(null);
  const { showToast } = useToast();

  useEffect(() => {
    setReconciliationStatus(isReconciled);
  }, [isReconciled]);

  const canReconcile = userRole === UserRole.Admin || userRole === UserRole.Underwriter;

  const handleReconcile = async () => {
    if (!canReconcile || loading || reconciliationStatus) return;

    setLoading(true);
    dispatch(reconcileStatementAccountAction({
      entityId: applicationId,
      entityType: 'application',
      statementId,
      accountId,
      bankProcessorKey,
      success: (res) => {
        const reconciliationResult = res.data;
        setDetails(reconciliationResult);
  
        if (reconciliationResult.isReconciled) {
          setReconciliationStatus(true);
          showToast('Account reconciled successfully', 'success');
        } else {
          // Display validation errors
          if (
            reconciliationResult.validationErrors &&
            reconciliationResult.validationErrors.length > 0
          ) {
            showToast({
              message: `${reconciliationResult.validationErrors.length} validation errors found`,
              type: 'error',
              items: reconciliationResult.validationErrors,
            });
          } else {
            // Fallback to simple difference message
            const differenceAmount = Math.abs(reconciliationResult.difference);
            const message =
              reconciliationResult.difference > 0
                ? `Found ${formatCurrency(
                    differenceAmount
                  )} in additional deposits`
                : `Found ${formatCurrency(
                    differenceAmount
                  )} in additional withdrawals`;
            showToast(message, 'error');
          }
        }
        setShowDetails(true);
        setLoading(false);
      },
      fail: (error) => {
        console.error('Error reconciling account:', error);
        showToast('Failed to reconcile account', 'error');
        setLoading(false);
      },
    }));
  };

  if (!canReconcile) return null;

  if (reconciliationStatus) {
    return (
      <Fragment>
        <button
          type="button"
          onClick={() => setShowDetails(true)}
          className="confidence-badge verified flex items-center gap-1 h-7 px-3 text-sm font-medium" // Removed py-1 since h-7 handles height
          style={{ width: '100%', justifyContent: 'center' }}
        >
          <CheckCircle2 className="w-4 h-4" size={16} />
          <span>Reconciled</span>
        </button>
        {details && (
          <Dialog open={showDetails} onOpenChange={setShowDetails}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Reconciliation Details</DialogTitle>
                <DialogDescription>
                  Account
                  {' '}
                  {accountNumber}
                  {' '}
                  was successfully reconciled
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <Card>
                  <CardContent className="pt-6">
                    <dl className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <dt className="font-medium text-gray-500">
                          Transactions Processed
                        </dt>
                        <dd className="mt-1 text-gray-900">
                          {details.transactionsProcessed}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-500">
                          Reconciliation Time
                        </dt>
                        <dd className="mt-1 text-gray-900">
                          {new Date(details.reconciliationTime).toLocaleString()}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-500">
                          Total Deposits
                        </dt>
                        <dd className="mt-1 text-green-600">
                          {formatCurrency(details.totalDeposits)}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-500">
                          Total Withdrawals
                        </dt>
                        <dd className="mt-1 text-red-600">
                          {formatCurrency(details.totalWithdrawals)}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-500">
                          Expected Balance
                        </dt>
                        <dd className="mt-1 text-gray-900">
                          {formatCurrency(details.expectedBalance)}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-500">
                          Calculated Balance
                        </dt>
                        <dd className="mt-1 text-gray-900">
                          {formatCurrency(details.calculatedBalance)}
                        </dd>
                      </div>
                    </dl>
                  </CardContent>
                </Card>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <button
        type="button"
        onClick={handleReconcile}
        disabled={loading}
        className="confidence-badge review flex items-center text-sm font-medium"
        style={{
          width: '100%',
          justifyContent: 'center',
          height: '32px', // Explicit height
          padding: '0 12px', // Consistent padding
        }}
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
        ) : null}
        Reconcile Now
      </button>
      {details && !reconciliationStatus && (
        <Dialog open={showDetails} onOpenChange={setShowDetails}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                Reconciliation Failed
              </DialogTitle>
              <DialogDescription>
                Found a discrepancy in account
                {' '}
                {accountNumber}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <Card>
                <CardContent className="pt-6">
                  <dl className="grid grid-cols-2 gap-4 text-sm">
                    <div className="col-span-2">
                      <dt className="font-medium text-red-500">Difference</dt>
                      <dd className="mt-1 text-red-600 text-lg font-medium">
                        {formatCurrency(Math.abs(details.difference))}
                        {details.difference > 0
                          ? ' Additional Deposits'
                          : ' Additional Withdrawals'}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-500">
                        Expected Balance
                      </dt>
                      <dd className="mt-1 text-gray-900">
                        {formatCurrency(details.expectedBalance)}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-500">
                        Calculated Balance
                      </dt>
                      <dd className="mt-1 text-gray-900">
                        {formatCurrency(details.calculatedBalance)}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-500">
                        Total Deposits
                      </dt>
                      <dd className="mt-1 text-green-600">
                        {formatCurrency(details.totalDeposits)}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-500">
                        Total Withdrawals
                      </dt>
                      <dd className="mt-1 text-red-600">
                        {formatCurrency(details.totalWithdrawals)}
                      </dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Fragment>
  );
};

ReconcileButton.propTypes = {
  statementId: PropTypes.string.isRequired,
  accountId: PropTypes.number.isRequired,
  userRole: PropTypes.string.isRequired,
  isReconciled: PropTypes.bool,
  accountNumber: PropTypes.string.isRequired,
};

export default ReconcileButton;
